/**
 * useHLPositionSync — Polls Hyperliquid open positions every 10s
 * when MetaMask wallet is active in live mode.
 * Reconciles HL positions with the store's position list.
 */

import { useEffect, useRef } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { generateId } from '@arc/shared';
import type { Position } from '@arc/shared';

const POLL_INTERVAL_MS = 10_000;

interface HLRawPosition {
  coin:       string;
  szi:        string;   // signed size (negative = short)
  entryPx:    string;
  markPx:     string;
  unrealizedPnl: string;
  leverage:   { value: number };
  positionValue: string;
}

interface HLPositionsResponse {
  assetPositions: Array<{ position: HLRawPosition }>;
}

export function useHLPositionSync() {
  const tradeMode    = useTerminalStore(s => s.tradeMode);
  const activeWallet = useTerminalStore(s => s.activeWallet);
  const openPosition = useTerminalStore(s => s.openPosition);
  const positions    = useTerminalStore(s => s.positions);
  const setLiveAccount = useTerminalStore(s => s.setLiveAccount);
  const liveAccount  = useTerminalStore(s => s.liveAccount);

  const walletRef   = useRef(activeWallet);
  const modeRef     = useRef(tradeMode);
  const posRef      = useRef(positions);
  const laRef       = useRef(liveAccount);
  useEffect(() => { walletRef.current = activeWallet; }, [activeWallet]);
  useEffect(() => { modeRef.current   = tradeMode;    }, [tradeMode]);
  useEffect(() => { posRef.current    = positions;    }, [positions]);
  useEffect(() => { laRef.current     = liveAccount;  }, [liveAccount]);

  useEffect(() => {
    const sync = async () => {
      const wallet = walletRef.current;
      if (!wallet || wallet.type !== 'metamask' || modeRef.current !== 'live') return;

      try {
        const res = await fetch(
          `/api/trading/hyperliquid/positions?address=${encodeURIComponent(wallet.address)}`
        );
        if (!res.ok) return;

        const json = await res.json() as { ok: boolean; data?: HLPositionsResponse };
        if (!json.ok || !json.data) return;

        const hlPositions = json.data.assetPositions
          .map(ap => ap.position)
          .filter(p => parseFloat(p.szi) !== 0);

        // Update live account position count
        const la = laRef.current;
        if (la) {
          setLiveAccount({ ...la, positions: hlPositions.length });
        }

        // Reconcile: add HL positions not already tracked
        const trackedSymbols = new Set(
          posRef.current
            .filter(p => p.mode === 'live' && p.exchange === 'hyperliquid')
            .map(p => p.symbol)
        );

        for (const hlPos of hlPositions) {
          if (trackedSymbols.has(hlPos.coin)) continue;

          const size     = parseFloat(hlPos.szi);
          const side     = size > 0 ? 'buy' : 'sell';
          const absSize  = Math.abs(size);
          const entry    = parseFloat(hlPos.entryPx);
          const mark     = parseFloat(hlPos.markPx);
          const upnl     = parseFloat(hlPos.unrealizedPnl);
          const upnlPct  = entry > 0 ? (upnl / (absSize * entry)) * 100 : 0;
          const posValue = parseFloat(hlPos.positionValue);

          const pos: Position = {
            id:               generateId(),
            orderId:          generateId(),
            symbol:           hlPos.coin,
            side,
            size:             posValue,
            entryPrice:       entry,
            currentPrice:     mark,
            unrealizedPnl:    upnl,
            unrealizedPnlPct: upnlPct,
            leverage:         hlPos.leverage?.value ?? 1,
            mode:             'live',
            exchange:         'hyperliquid',
            openedAt:         Date.now(),
          };

          openPosition(pos);
        }
      } catch { /* ignore */ }
    };

    const id = setInterval(sync, POLL_INTERVAL_MS);
    sync();
    return () => clearInterval(id);
  }, [openPosition, setLiveAccount]);
}
